import { Link } from "react-router";
import i18n from "../../i18n";
import { CompanyChip, PipelineMini, StatusPill } from "../../design/components";
import {
  INBOUND_STAGES,
  IN_STAGE_LABEL,
  OUTBOUND_STAGES,
  stageIndex,
  type InboundStage,
  type OutboundStage,
} from "../../domain/stages";
import { omr, relative } from "../../lib/format";
import type { DocumentSummary } from "../../api/types";
import { stageLabel } from "./StageStrip";
import { RowActions } from "./RowActions";
import { DocTypeCell } from "./DocTypeCell";
import styles from "./DocumentsQueue.module.css";

/** Both vocabularies of ruling 50: outbound done/error, inbound held/landed. */
export const STATE_PILL: Record<string, { status: string; label: string }> = {
  in_flight: { status: "active", label: "In flight" },
  done: { status: "completed", label: "Completed" },
  error: { status: "error", label: "Error" },
  held: { status: "held", label: "Held" },
  landed: { status: "completed", label: "Landed" },
};

const STATE_LABEL_KEY: Record<string, string> = {
  in_flight: "documents.state.inFlight",
  done: "documents.state.done",
  error: "documents.state.error",
  held: "documents.state.held",
  landed: "documents.state.landed",
};

/** Used both inside and outside React render (other features' rows), so it reads the
 * i18next singleton directly rather than the useTranslation hook. */
export function statePill(state: string): { status: string; label: string } {
  const entry = STATE_PILL[state];
  if (!entry) return { status: state, label: state };
  const key = STATE_LABEL_KEY[state];
  return { status: entry.status, label: key ? i18n.t(key, entry.label) : entry.label };
}

function isOutboundStage(stage: string): stage is OutboundStage {
  return (OUTBOUND_STAGES as readonly string[]).includes(stage);
}

function isInboundStage(stage: string): stage is InboundStage {
  return (INBOUND_STAGES as readonly string[]).includes(stage);
}

/** PipelineMini only knows the outbound stages, so inbound rows show their stage label. */
export function StageCell({ row }: { row: DocumentSummary }) {
  if (row.direction === "in") {
    return <span>{isInboundStage(row.stage) ? IN_STAGE_LABEL[row.stage] : row.stage}</span>;
  }
  const index = isOutboundStage(row.stage) ? stageIndex(row.stage) : 0;
  return row.state === "error" ? <PipelineMini index={index} state="failed" /> : <PipelineMini index={index} />;
}

export function DocumentRow({
  row,
  onActionsOpenChange,
}: {
  row: DocumentSummary;
  onActionsOpenChange?: ((open: boolean) => void) | undefined;
}) {
  const amount = row.totalAmount === null ? null : Number(row.totalAmount);
  return (
    <tr>
      <td className="nowrap">
        <CompanyChip entityId={row.entityId} name={row.entityName} code={row.entityCode} />
      </td>
      <td className={styles.docId}>
        <Link className="link" to={`/documents/${row.id}`}>
          {row.erpDocId ?? "—"}
        </Link>
        {row.uuid ? <div className={`mono ${styles.uuid}`}>{row.uuid}</div> : null}
      </td>
      <td className="nowrap">
        <DocTypeCell typeCode={row.typeCode} flags={row.flags} direction={row.direction} />
      </td>
      <td className={styles.counterparty} title={row.counterparty ?? undefined}>
        {row.counterparty ?? "—"}
      </td>
      <td className="nowrap">
        <span className="row-tight">
          <StageCell row={row} />
          <span className={styles.stageText}>{stageLabel(row.stage, row.direction)}</span>
        </span>
      </td>
      <td className="nowrap">
        <StatusPill {...statePill(row.state)} />
        {row.state === "error" && row.errorCode ? (
          <div className={`mono ${styles.errorCode}`}>{row.errorCode}</div>
        ) : null}
      </td>
      <td className="r">{row.attempts}</td>
      <td className="r nowrap">
        {omr(amount)}
        {row.currency ? ` ${row.currency}` : ""}
      </td>
      <td className="nowrap">{relative(row.updatedAt)}</td>
      <td className={styles.actionsCell}>
        <RowActions row={row} onOpenChange={onActionsOpenChange} />
      </td>
    </tr>
  );
}
